import React, { createContext, useContext, useReducer, useEffect, useCallback, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { socketService } from '../services/socketService';
import { notificationAPI } from '../services/api';
import { showDesktopNotification, getPermissionState, requestPermission, PermissionState } from '../services/browserNotificationService';
import toast from 'react-hot-toast';

// Types
export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

// Action Types
type NotificationAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: Notification[] }
  | { type: 'FETCH_FAILURE'; payload: string }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'MARK_AS_READ'; payload: string }
  | { type: 'MARK_ALL_AS_READ' }
  | { type: 'DELETE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_NOTIFICATIONS' };

// Initial State
const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

// Reducer
const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'FETCH_SUCCESS':
      const notifications = action.payload || [];
      const unreadCount = notifications.filter(n => !n.isRead).length;
      return {
        ...state,
        notifications,
        unreadCount,
        loading: false,
        error: null,
      };
    case 'FETCH_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'ADD_NOTIFICATION':
      const newNotifications = [action.payload, ...state.notifications];
      const newUnreadCount = newNotifications.filter(n => !n.isRead).length;
      return {
        ...state,
        notifications: newNotifications,
        unreadCount: newUnreadCount,
      };
    case 'MARK_AS_READ':
      const updatedNotifications = state.notifications.map(n =>
        n.id === action.payload ? { ...n, isRead: true } : n
      );
      const updatedUnreadCount = updatedNotifications.filter(n => !n.isRead).length;
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedUnreadCount,
      };
    case 'MARK_ALL_AS_READ':
      const allReadNotifications = state.notifications.map(n => ({ ...n, isRead: true }));
      return {
        ...state,
        notifications: allReadNotifications,
        unreadCount: 0,
      };
    case 'DELETE_NOTIFICATION':
      const filteredNotifications = state.notifications.filter(n => n.id !== action.payload);
      const filteredUnreadCount = filteredNotifications.filter(n => !n.isRead).length;
      return {
        ...state,
        notifications: filteredNotifications,
        unreadCount: filteredUnreadCount,
      };
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
      };
    default:
      return state;
  }
};

// Context
interface NotificationContextType extends NotificationState {
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearNotifications: () => void;
  browserPermission: PermissionState;
  requestBrowserPermission: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Provider Props
interface NotificationProviderProps {
  children: ReactNode;
}

// Provider Component
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { user } = useAuth();
  const [browserPermission, setBrowserPermission] = useState<PermissionState>(getPermissionState());

  // Request browser notification permission
  const requestBrowserPermission = useCallback(async () => {
    const result = await requestPermission();
    setBrowserPermission(result);
  }, []);

  // Get deep-link URL based on notification type
  const getNotificationUrl = (notification: Notification): string => {
    const data = notification.data;
    switch (notification.type) {
      case 'NEW_OFFER':
        return data?.requestId ? `/requests/${data.requestId}` : '/requests';
      case 'OFFER_ACCEPTED':
      case 'OFFER_REJECTED':
        return '/offers';
      case 'REQUEST_EXPIRED':
        return '/requests';
      case 'SELLER_APPROVED':
      case 'SELLER_REJECTED':
        return '/seller/dashboard';
      default:
        return '/notifications';
    }
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'FETCH_START' });
      
      const response = await notificationAPI.getNotifications();
      
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: response.data.data.notifications, // Corregido: backend devuelve 'notifications'
      });
    } catch (error: any) {
      console.error('Failed to fetch notifications:', error);
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.response?.data?.message || 'Error al cargar notificaciones',
      });
    }
  }, []); // Empty dependency array since it doesn't depend on any props or state

  // Fetch notifications when user is authenticated
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      dispatch({ type: 'CLEAR_NOTIFICATIONS' });
    }
  }, [user, fetchNotifications]);

  // Setup socket listeners
  useEffect(() => {
    if (user) {
      // Listen for new notifications
      socketService.onNotification((notification: Notification) => {
        dispatch({ type: 'ADD_NOTIFICATION', payload: notification });

        // Show toast notification
        toast(notification.message, {
          icon: getNotificationIcon(notification.type),
          duration: 5000,
        });

        // Show desktop notification when tab is not focused
        showDesktopNotification(notification.title, {
          body: notification.message,
          tag: `notif-${notification.id}`,
          data: { url: getNotificationUrl(notification) },
        });
      });
    }

    return () => {
      socketService.removeNotificationListeners();
    };
  }, [user]);

  // Get notification icon based on type
  const getNotificationIcon = (type: Notification['type']): string => {
    switch (type) {
      case 'NEW_OFFER':
        return '💰';
      case 'OFFER_ACCEPTED':
        return '✅';
      case 'OFFER_REJECTED':
        return '❌';
      case 'REQUEST_EXPIRED':
      case 'OFFER_EXPIRED':
        return '⏰';
      case 'SELLER_APPROVED':
        return '🎉';
      case 'SELLER_REJECTED':
        return '😞';
      case 'SYSTEM':
        return 'ℹ️';
      default:
        return '🔔';
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      await notificationAPI.markAsRead(notificationId);
      dispatch({ type: 'MARK_AS_READ', payload: notificationId });
    } catch (error: any) {
      console.error('Failed to mark notification as read:', error);
      toast.error('Error al marcar notificación como leída');
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async (): Promise<void> => {
    try {
      await notificationAPI.markAllAsRead();
      dispatch({ type: 'MARK_ALL_AS_READ' });
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error: any) {
      console.error('Failed to mark all notifications as read:', error);
      toast.error('Error al marcar todas las notificaciones como leídas');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string): Promise<void> => {
    try {
      await notificationAPI.deleteNotification(notificationId);
      dispatch({ type: 'DELETE_NOTIFICATION', payload: notificationId });
      toast.success('Notificación eliminada');
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
      toast.error('Error al eliminar notificación');
    }
  };

  // Clear all notifications (local only)
  const clearNotifications = (): void => {
    dispatch({ type: 'CLEAR_NOTIFICATIONS' });
  };

  const value: NotificationContextType = {
    ...state,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearNotifications,
    browserPermission,
    requestBrowserPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;